/**
 * Designer Detail Page
 * Shows a designer's full profile when a homeowner clicks from the browse page.
 * Homeowners can send a project request directly from this page.
 */

import type { TargetReviewsResponse } from "@shared/types";
import { useQueryClient } from "@tanstack/react-query";
import {
	ArrowLeft,
	ArrowRight,
	BadgeCheck,
	Briefcase,
	CircleCheck,
	Clock,
	ImageIcon,
	IndianRupee,
	MapPin,
	Palette,
	Send,
	X,
} from "lucide-react";
import type * as React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardTitle,
} from "@/components/ui/card";
import { ImageLightbox } from "@/components/ui/image-lightbox";
import { Label } from "@/components/ui/label";
import { ReviewSummarySection } from "@/components/ui/review-summary-section";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuthStore } from "@/features/auth/store/auth.store";
import {
	type DesignerProfile,
	getDesignerById,
} from "@/features/marketplace/services/marketplace-api.service";
import {
	getProjectProposals,
	getProjects,
	type Project,
	sendRequestToDesigner,
} from "@/features/projects/services/projects-api.service";
import { formatPrice } from "@/lib/format";
import { queryKeys } from "@/lib/query-keys";
import { cn, resolveMediaUrl } from "@/lib/utils";
import { fetchDesignerReviews } from "@/services/reviews-api.service";

// ============================================================================
// HELPERS
// ============================================================================

// ============================================================================
// LOADING SKELETON
// ============================================================================

function DesignerDetailSkeleton(): React.ReactElement {
	return (
		<div className="min-h-screen bg-muted">
			<div className="bg-card border-b">
				<div className="mx-auto max-w-4xl px-4 py-4">
					<Skeleton className="h-10 w-24" />
				</div>
			</div>
			<div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
				{/* Profile header skeleton */}
				<Card className="p-6">
					<div className="flex items-start gap-4">
						<Skeleton className="h-20 w-20 rounded-full" />
						<div className="flex-1 space-y-2">
							<Skeleton className="h-7 w-48" />
							<Skeleton className="h-5 w-32" />
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-3/4" />
						</div>
					</div>
				</Card>

				{/* Stats skeleton */}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					<Skeleton className="h-24 rounded-xl" />
					<Skeleton className="h-24 rounded-xl" />
					<Skeleton className="h-24 rounded-xl" />
				</div>

				{/* Details skeleton */}
				<Card className="p-6 space-y-4">
					<Skeleton className="h-6 w-40" />
					<div className="flex flex-wrap gap-2">
						<Skeleton className="h-8 w-24 rounded-full" />
						<Skeleton className="h-8 w-20 rounded-full" />
						<Skeleton className="h-8 w-28 rounded-full" />
					</div>
				</Card>

				{/* Portfolio skeleton */}
				<Card className="p-6 space-y-4">
					<Skeleton className="h-6 w-32" />
					<div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
						<Skeleton className="aspect-square rounded-lg" />
						<Skeleton className="aspect-square rounded-lg" />
						<Skeleton className="aspect-square rounded-lg" />
					</div>
				</Card>
			</div>
		</div>
	);
}

// ============================================================================
// NOT FOUND STATE
// ============================================================================

function DesignerNotFound(): React.ReactElement {
	const { t } = useTranslation();
	const navigate = useNavigate();

	return (
		<div className="min-h-screen bg-muted">
			<div className="bg-card border-b">
				<div className="mx-auto max-w-4xl px-4 py-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate("/designers")}
					>
						<ArrowLeft className="h-4 w-4" />
						{t("designerDetail.backToDesigners")}
					</Button>
				</div>
			</div>
			<div className="mx-auto max-w-4xl px-4 py-12">
				<Card className="p-12 text-center">
					<CardContent className="space-y-4 p-0">
						<div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-muted">
							<Briefcase className="h-8 w-8 text-muted-foreground" />
						</div>
						<CardTitle className="text-xl">
							{t("designerDetail.designerNotFound")}
						</CardTitle>
						<CardDescription className="text-base">
							{t("designerDetail.designerNotFoundDesc")}
						</CardDescription>
						<Button variant="primary" onClick={() => navigate("/designers")}>
							{t("designerDetail.browseDesigners")}
						</Button>
					</CardContent>
				</Card>
			</div>
		</div>
	);
}

// ============================================================================
// SEND REQUEST DIALOG (Card Overlay)
// ============================================================================

interface SendRequestDialogProps {
	designer: DesignerProfile;
	onClose: () => void;
	onSuccess: () => void;
}

function SendRequestDialog({
	designer,
	onClose,
	onSuccess,
}: SendRequestDialogProps): React.ReactElement {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const [projects, setProjects] = useState<Project[]>([]);
	const [selectedProjectId, setSelectedProjectId] = useState<string>("");
	const [message, setMessage] = useState("");
	const [isLoadingProjects, setIsLoadingProjects] = useState(true);
	const [isSending, setIsSending] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		async function fetchProjects(): Promise<void> {
			setIsLoadingProjects(true);
			try {
				const allProjects = await getProjects();
				// Only show projects that are in a state where a designer can be requested
				// AND where this designer is not already assigned
				const eligibleProjects = allProjects.filter(
					(p) =>
						(p.status === "draft" || p.status === "seeking_designer") &&
						p.designerId !== designer.id,
				);
				setProjects(eligibleProjects);
				if (eligibleProjects.length > 0) {
					setSelectedProjectId(eligibleProjects[0].id);
				}
			} catch {
				setError(t("designerDetail.failedToLoadProjects"));
			} finally {
				setIsLoadingProjects(false);
			}
		}
		fetchProjects();
	}, [t, designer.id]);

	async function handleSendRequest(): Promise<void> {
		if (!selectedProjectId) {
			setError(t("designerDetail.pleaseSelectProject"));
			return;
		}

		setIsSending(true);
		setError(null);

		try {
			const result = await sendRequestToDesigner({
				projectId: selectedProjectId,
				designerId: designer.id,
				message: message.trim() || undefined,
			});

			if (result.success) {
				// The project's detail prefix holds its requests and proposals,
				// which now include this request; the list and the project
				// itself go stale too, because a request moves a draft project to
				// seeking_designer.
				void queryClient.invalidateQueries({
					queryKey: queryKeys.projects.detail(selectedProjectId),
				});
				void queryClient.invalidateQueries({
					queryKey: queryKeys.projects.lists(),
				});
				onSuccess();
			} else {
				setError(result.error || t("designerDetail.failedToSendRequest"));
			}
		} catch {
			setError(t("designerDetail.failedToSendRequest"));
		} finally {
			setIsSending(false);
		}
	}

	return (
		<div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-xs">
			<div className="w-full max-w-md rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-4)]">
				<div className="flex items-center justify-between mb-2">
					<h2 className="font-display text-[20px] font-medium text-dashboard-text">
						{t("designerDetail.sendRequestTitle")}
					</h2>
					<Button
						variant="ghost"
						size="icon"
						onClick={onClose}
						className="rounded-full hover:bg-dashboard-surface"
					>
						<X className="h-5 w-5 text-dashboard-muted" />
					</Button>
				</div>
				<p className="font-body text-xs text-dashboard-muted mb-5 leading-relaxed">
					{t("designerDetail.sendRequestDesc", { name: designer.name })}
				</p>

				<div className="space-y-4 mb-6">
					{/* Project Selection */}
					<div className="space-y-2">
						<Label className="font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted">
							{t("designerDetail.selectProject")}
						</Label>
						{isLoadingProjects ? (
							<Skeleton className="h-10 w-full rounded-xl" />
						) : projects.length === 0 ? (
							<div className="rounded-xl border border-dashed border-dashboard-border p-4 text-center">
								<p className="font-body text-sm text-dashboard-muted">
									{t("designerDetail.noEligibleProjects")}
								</p>
							</div>
						) : (
							<div className="space-y-2 max-h-52 overflow-y-auto pr-1">
								{projects.map((project) => (
									<button
										key={project.id}
										type="button"
										className={cn(
											"w-full text-left cursor-pointer rounded-xl border p-3.5 transition-all duration-200",
											selectedProjectId === project.id
												? "border-dashboard-accent bg-dashboard-surface/50 shadow-[var(--dashboard-shadow-1)]"
												: "border-dashboard-border hover:bg-dashboard-surface/30",
										)}
										onClick={() => setSelectedProjectId(project.id)}
									>
										<div className="flex items-center gap-3">
											<div
												className={cn(
													"h-4 w-4 shrink-0 rounded-full border-2 transition-colors",
													selectedProjectId === project.id
														? "border-dashboard-accent bg-dashboard-accent"
														: "border-dashboard-muted",
												)}
											/>
											<div className="min-w-0">
												<p className="font-display text-sm font-medium text-dashboard-text truncate">
													{project.title}
												</p>
												<p className="font-mono text-[9px] uppercase tracking-wider text-dashboard-muted mt-0.5">
													{project.scope === "full_home"
														? t("common.fullHome")
														: t("common.partial")}
													{project.budgetMin && project.budgetMax && (
														<>
															{" "}
															&middot; {formatPrice(project.budgetMin)} -{" "}
															{formatPrice(project.budgetMax)}
														</>
													)}
												</p>
											</div>
										</div>
									</button>
								))}
							</div>
						)}
					</div>

					{/* Message */}
					<div className="space-y-2">
						<Label
							htmlFor="request-message"
							className="font-mono text-[9px] font-bold uppercase tracking-widest text-dashboard-muted"
						>
							{t("designerDetail.messageOptional")}
						</Label>
						<Textarea
							id="request-message"
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							placeholder={t("designerDetail.messagePlaceholder")}
							rows={3}
							className="resize-none rounded-xl border-dashboard-border bg-dashboard-bg text-dashboard-text font-body text-sm shadow-[var(--dashboard-shadow-1)] focus-visible:ring-dashboard-accent focus-visible:border-dashboard-accent"
						/>
					</div>

					{/* Error */}
					{error && (
						<div className="rounded-xl border border-destructive/20 bg-destructive/10 p-3">
							<p className="text-sm text-destructive font-body">{error}</p>
						</div>
					)}
				</div>

				<div className="flex gap-3">
					<Button
						variant="outline"
						onClick={onClose}
						className="flex-1 rounded-xl border border-dashboard-border bg-dashboard-paper hover:bg-dashboard-surface font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-text h-10 transition-colors shadow-[var(--dashboard-shadow-1)]"
					>
						{t("common.cancel")}
					</Button>
					<Button
						variant="primary"
						onClick={handleSendRequest}
						isLoading={isSending}
						disabled={projects.length === 0 || !selectedProjectId}
						className="flex-1 rounded-xl border-none bg-dashboard-accent hover:bg-dashboard-accent-hover font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-on-fill h-10 transition-all shadow-[var(--dashboard-shadow-1)]"
					>
						<Send className="h-4 w-4" />
						{t("designerDetail.sendRequest")}
					</Button>
				</div>
			</div>
		</div>
	);
}

// ============================================================================
// MAIN PAGE COMPONENT
// ============================================================================

export function DesignerDetailPage(): React.ReactElement {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { user } = useAuthStore();

	const [designer, setDesigner] = useState<DesignerProfile | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [notFound, setNotFound] = useState(false);
	const [showRequestDialog, setShowRequestDialog] = useState(false);
	const [requestSent, setRequestSent] = useState(false);
	const [alreadyAssignedProject, setAlreadyAssignedProject] =
		useState<Project | null>(null);
	const [alreadyRequestedProject, setAlreadyRequestedProject] = useState<{
		project: Project;
		status: "pending" | "proposal_submitted";
	} | null>(null);
	const [reviews, setReviews] = useState<TargetReviewsResponse>({
		summary: { averageRating: null, reviewCount: 0 },
		reviews: [],
	});
	const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
	const [reviewsFailed, setReviewsFailed] = useState(false);

	const isHomeowner = user?.userType === "homeowner";
	const isHomeownerRef = useRef(isHomeowner);
	useEffect(() => {
		isHomeownerRef.current = isHomeowner;
	}, [isHomeowner]);

	useEffect(() => {
		let cancelled = false;

		// Reset states to prevent flashing stale data
		setDesigner(null);
		setReviews({
			summary: { averageRating: null, reviewCount: 0 },
			reviews: [],
		});
		setAlreadyAssignedProject(null);
		setAlreadyRequestedProject(null);
		setNotFound(false);
		setLightboxIndex(null);
		setReviewsFailed(false);

		async function fetchDesigner(): Promise<void> {
			if (!id) {
				setNotFound(true);
				setIsLoading(false);
				return;
			}

			setIsLoading(true);
			const result = await getDesignerById(id);
			if (cancelled) return;

			if (result) {
				setDesigner(result);
				try {
					const reviewResult = await fetchDesignerReviews(id);
					if (cancelled) return;
					setReviews(reviewResult);
				} catch (err) {
					if (cancelled) return;
					console.error("Failed to fetch designer reviews:", err);
					setReviewsFailed(true);
				}

				// Check if this designer is already working on any of the user's projects
				try {
					const allProjects = await getProjects();
					if (cancelled) return;
					// Check for any project where this designer is assigned (regardless of status)
					// Use p.designer?.id for the enriched designer object
					const assignedProject = allProjects.find(
						(p) => p.designer?.id === result.id,
					);
					if (assignedProject && isHomeownerRef.current) {
						setAlreadyAssignedProject(assignedProject);
					} else if (isHomeownerRef.current) {
						const eligibleProjects = allProjects.filter(
							(p) => p.status === "draft" || p.status === "seeking_designer",
						);
						const proposalsPromises = eligibleProjects.map(async (proj) => {
							try {
								const proposals = await getProjectProposals(proj.id);
								const pendingReq = proposals.find(
									(p) =>
										p.designer.id === result.id &&
										(p.request.status === "pending" ||
											p.request.status === "proposal_submitted"),
								);
								if (pendingReq) {
									return {
										project: proj,
										status: pendingReq.request.status as
											| "pending"
											| "proposal_submitted",
									};
								}
							} catch {
								// ignore
							}
							return null;
						});

						const results = await Promise.all(proposalsPromises);
						if (cancelled) return;

						const foundPending = results.find((r) => r !== null);
						if (foundPending) {
							setAlreadyRequestedProject(foundPending);
						}
					}
				} catch {
					// Ignore errors when checking assigned projects
				}
			} else {
				setNotFound(true);
			}

			setIsLoading(false);
		}

		fetchDesigner();

		return () => {
			cancelled = true;
		};
	}, [id]);

	// Loading state
	if (isLoading) {
		return <DesignerDetailSkeleton />;
	}

	// Not found state
	if (notFound || !designer) {
		return <DesignerNotFound />;
	}

	const portfolioImages = designer.portfolioImages ?? [];

	function handleRequestSuccess(): void {
		setShowRequestDialog(false);
		setRequestSent(true);
	}

	return (
		<div
			className="min-h-screen pb-12"
			style={{
				background:
					"radial-gradient(ellipse at 0% 0%, color-mix(in srgb, var(--color-dashboard-accent-light) 6%, transparent) 0%, transparent 45%), radial-gradient(ellipse at 100% 100%, color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent) 0%, transparent 50%), var(--color-dashboard-surface)",
			}}
		>
			{/* Top Navigation Bar */}
			<div className="border-b border-dashboard-border/60 bg-transparent">
				<div className="mx-auto max-w-4xl px-4 py-4 flex items-center justify-between">
					<Button
						variant="ghost"
						size="sm"
						onClick={() => navigate("/designers")}
						className="font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-text hover:bg-dashboard-surface/65"
					>
						<ArrowLeft className="h-4 w-4 text-dashboard-accent-dark" />
						{t("designerDetail.backToDesigners")}
					</Button>

					{isHomeowner &&
						!requestSent &&
						!alreadyAssignedProject &&
						!alreadyRequestedProject && (
							<Button
								variant="primary"
								size="sm"
								onClick={() => setShowRequestDialog(true)}
								className="h-9 rounded-xl border-none px-4 font-mono text-[10px] uppercase font-bold tracking-wider text-dashboard-on-fill bg-dashboard-accent hover:bg-dashboard-accent-hover transition-all duration-200 shadow-[var(--dashboard-shadow-1)]"
							>
								<Send className="h-4 w-4" />
								{t("designerDetail.sendRequest")}
							</Button>
						)}
				</div>
			</div>

			<div className="mx-auto max-w-4xl px-4 py-6 space-y-6">
				{/* Success banner after sending request */}
				{requestSent && (
					<div className="rounded-2xl border border-dashboard-success-border bg-dashboard-success-bg/70 p-5 shadow-[var(--dashboard-shadow-1)] backdrop-blur-md">
						<div className="flex items-start gap-3.5">
							<CircleCheck className="h-5 w-5 text-dashboard-pill-done shrink-0 mt-0.5" />
							<div>
								<p className="font-display text-sm font-semibold text-dashboard-success-strong">
									{t("designerDetail.requestSentSuccess")}
								</p>
								<p className="font-body text-sm text-dashboard-success mt-1">
									{t("designerDetail.requestSentDesc", { name: designer.name })}
								</p>
							</div>
						</div>
					</div>
				)}

				{/* Profile Header Card */}
				<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-2)]">
					<div className="flex flex-col sm:flex-row items-start gap-5">
						{/* Avatar */}
						<div
							className="flex h-20 w-20 shrink-0 items-center justify-center rounded-full text-2xl font-display font-bold border border-dashboard-border shadow-[var(--dashboard-shadow-avatar)]"
							style={{
								background: designer.profilePictureUrl
									? "transparent"
									: "linear-gradient(135deg, var(--color-dashboard-warm-200), var(--color-dashboard-orange-300))",
								color: "var(--color-dashboard-orange-text-on-tint)",
							}}
						>
							{designer.profilePictureUrl ? (
								<img
									src={resolveMediaUrl(designer.profilePictureUrl)}
									alt={designer.name}
									className="h-20 w-20 rounded-full object-cover"
								/>
							) : (
								designer.name.charAt(0).toUpperCase()
							)}
						</div>

						{/* Name and Info */}
						<div className="flex-1 min-w-0">
							<div className="flex items-center gap-2">
								<h1 className="font-display text-[28px] font-medium text-dashboard-text tracking-tight">
									{designer.name}
								</h1>
								{designer.isVerified && (
									<BadgeCheck className="h-6 w-6 text-dashboard-accent-dark shrink-0" />
								)}
							</div>

							{designer.firmName && (
								<p className="font-body text-sm font-medium text-dashboard-muted mt-0.5">
									{designer.firmName}
								</p>
							)}

							{designer.bio && (
								<p className="mt-3.5 font-body text-[14px] text-dashboard-muted-dark leading-relaxed">
									{designer.bio}
								</p>
							)}
						</div>
					</div>
				</div>

				{/* Stats Row */}
				<div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
					{/* Experience */}
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
						<div className="flex items-center gap-4">
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dashboard-surface">
								<Clock className="h-5 w-5 text-dashboard-accent-dark" />
							</div>
							<div>
								<p className="font-mono text-[9px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("designerDetail.experience")}
								</p>
								<p className="font-display text-[20px] font-medium text-dashboard-text mt-0.5">
									{designer.experienceYears
										? t("designerDetail.xYears", {
												count: designer.experienceYears,
											})
										: t("project.notSpecified")}
								</p>
							</div>
						</div>
					</div>

					{/* Projects Completed */}
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
						<div className="flex items-center gap-4">
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dashboard-surface">
								<CircleCheck className="h-5 w-5 text-dashboard-accent-dark" />
							</div>
							<div>
								<p className="font-mono text-[9px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("designerDetail.projectsCompleted")}
								</p>
								<p className="font-display text-[20px] font-medium text-dashboard-text mt-0.5">
									{designer.projectsCompleted != null
										? designer.projectsCompleted
										: t("project.notSpecified")}
								</p>
							</div>
						</div>
					</div>

					{/* Price Range */}
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-5 shadow-[var(--dashboard-shadow-1)]">
						<div className="flex items-center gap-4">
							<div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-dashboard-surface">
								<IndianRupee className="h-5 w-5 text-dashboard-accent-dark" />
							</div>
							<div>
								<p className="font-mono text-[9px] font-semibold uppercase tracking-widest text-dashboard-muted">
									{t("common.priceRange")}
								</p>
								<p className="font-display text-[20px] font-medium text-dashboard-text mt-0.5">
									{designer.priceRangeMin && designer.priceRangeMax
										? `${formatPrice(designer.priceRangeMin)} - ${formatPrice(designer.priceRangeMax)}`
										: t("project.notSpecified")}
								</p>
							</div>
						</div>
					</div>
				</div>

				<ReviewSummarySection
					title="Client Reviews"
					summary={reviews.summary}
					reviews={reviews.reviews}
					emptyMessage="No homeowner reviews yet."
					loadFailed={
						reviewsFailed
							? {
									message: t("reviews.loadFailed"),
									retryLabel: t("reviews.retry"),
									onRetry: () => {
										setReviewsFailed(false);
										fetchDesignerReviews(designer.id)
											.then(setReviews)
											.catch((err: unknown) => {
												console.error("Failed to fetch designer reviews:", err);
												setReviewsFailed(true);
											});
									},
								}
							: undefined
					}
				/>

				{/* Service Cities */}
				{designer.serviceCities && designer.serviceCities.length > 0 && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<MapPin className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("designerDetail.serviceCities")}
						</h2>
						<div className="flex flex-wrap gap-2">
							{designer.serviceCities.map((city) => (
								<span
									key={city}
									className="inline-flex items-center rounded-full bg-dashboard-surface border border-dashboard-border px-3 py-1 font-body text-xs text-dashboard-text"
								>
									{city}
								</span>
							))}
						</div>
					</div>
				)}

				{/* Design Styles */}
				{designer.styles && designer.styles.length > 0 && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<Palette className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("designerDetail.designStyles")}
						</h2>
						<div className="flex flex-wrap gap-2">
							{designer.styles.map((style) => (
								<span
									key={style}
									className="inline-flex items-center rounded-full bg-dashboard-surface border border-dashboard-border px-3 py-1 font-body text-xs text-dashboard-accent-dark font-semibold"
								>
									{style}
								</span>
							))}
						</div>
					</div>
				)}

				{/* Services Offered */}
				{designer.services && designer.services.length > 0 && (
					<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text border-b border-dashboard-divider pb-2.5 mb-4 flex items-center gap-2">
							<Briefcase className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("designerDetail.servicesOffered")}
						</h2>
						<ul className="space-y-2.5">
							{designer.services.map((service) => (
								<li
									key={service}
									className="flex items-center gap-2.5 text-sm font-body text-dashboard-text"
								>
									<CircleCheck className="h-4 w-4 text-dashboard-accent-dark shrink-0" />
									{service}
								</li>
							))}
						</ul>
					</div>
				)}

				{/* Portfolio */}
				<div className="rounded-2xl border border-dashboard-border bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-1)]">
					<div className="border-b border-dashboard-divider pb-2.5 mb-4 flex items-center justify-between gap-3">
						<h2 className="font-display text-[18px] font-medium text-dashboard-text flex items-center gap-2">
							<ImageIcon className="h-4.5 w-4.5 text-dashboard-accent-dark" />
							{t("designerDetail.portfolio")}
						</h2>
						{designer.portfolioSlug && (
							<Link
								to={`/portfolio/${encodeURIComponent(designer.portfolioSlug)}`}
								className="inline-flex items-center gap-1.5 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-accent-dark hover:text-dashboard-accent-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dashboard-accent rounded"
							>
								{t("designerDetail.viewFullPortfolio")}
								<ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
							</Link>
						)}
					</div>
					{portfolioImages.length > 0 ? (
						<div className="grid grid-cols-2 gap-3.5 sm:grid-cols-3">
							{portfolioImages.map((imageUrl, index) => (
								<button
									key={imageUrl}
									type="button"
									onClick={() => setLightboxIndex(index)}
									aria-label={t("designerDetail.openPhoto", {
										index: index + 1,
										total: portfolioImages.length,
									})}
									className="overflow-hidden rounded-xl border border-dashboard-border/50 bg-dashboard-bg group/portfolio cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dashboard-accent focus-visible:ring-offset-2"
								>
									<img
										src={resolveMediaUrl(imageUrl)}
										alt=""
										className="aspect-square w-full object-cover transition-transform duration-500 group-hover/portfolio:scale-105"
									/>
								</button>
							))}
						</div>
					) : (
						<div className="border border-dashed border-dashboard-border rounded-xl p-8 text-center bg-dashboard-bg/50">
							<div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-dashboard-surface">
								<ImageIcon className="h-6 w-6 text-dashboard-accent-dark" />
							</div>
							<p className="mt-3 text-sm font-body text-dashboard-muted">
								{t("designerDetail.noPortfolioYet")}
							</p>
						</div>
					)}
				</div>

				{/* Bottom CTA for homeowners */}
				{isHomeowner &&
					!requestSent &&
					!alreadyAssignedProject &&
					!alreadyRequestedProject && (
						<div className="rounded-2xl border border-dashboard-accent/30 bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-2)]">
							<div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
								<div>
									<p className="font-display text-lg font-medium text-dashboard-text">
										{t("designerDetail.interestedInWorking", {
											name: designer.name,
										})}
									</p>
									<p className="font-body text-sm text-dashboard-muted mt-1">
										{t("designerDetail.sendRequestCta")}
									</p>
								</div>
								<Button
									variant="primary"
									onClick={() => setShowRequestDialog(true)}
									className="shrink-0 h-10 rounded-xl px-5 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-on-fill bg-dashboard-accent hover:bg-dashboard-accent-hover transition-all shadow-[var(--dashboard-shadow-1)] border-none"
								>
									<Send className="h-4 w-4" />
									{t("designerDetail.sendRequest")}
								</Button>
							</div>
						</div>
					)}

				{/* Already working on a project indicator */}
				{isHomeowner && alreadyAssignedProject && (
					<div className="rounded-2xl border border-dashboard-success-border bg-dashboard-success-bg p-6 shadow-[var(--dashboard-shadow-2)]">
						<div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-dashboard-pill-done-bg">
									<CircleCheck className="h-5 w-5 text-dashboard-pill-done" />
								</div>
								<div>
									<p className="font-display text-lg font-medium text-dashboard-success-strong">
										{t("designerDetail.alreadyWorkingTogether")}
									</p>
									<p className="font-body text-sm text-dashboard-pill-done mt-0.5">
										{t("designerDetail.alreadyWorkingOnProject", {
											designerName: designer.name,
											projectTitle: alreadyAssignedProject.title,
										})}
									</p>
								</div>
							</div>
							<Button
								variant="primary"
								onClick={() =>
									navigate(`/projects/${alreadyAssignedProject.id}`)
								}
								className="shrink-0 h-10 rounded-xl px-5 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-on-fill bg-green-600 hover:bg-green-700 transition-all shadow-[var(--dashboard-shadow-1)] border-none"
							>
								{t("designerDetail.viewProject")}
							</Button>
						</div>
					</div>
				)}

				{/* Already requested on a project indicator */}
				{isHomeowner && !alreadyAssignedProject && alreadyRequestedProject && (
					<div className="rounded-2xl border border-dashboard-accent/30 bg-dashboard-paper p-6 shadow-[var(--dashboard-shadow-2)] mt-6">
						<div className="flex flex-col items-center gap-3 sm:flex-row sm:justify-between">
							<div className="flex items-center gap-3">
								<div className="flex h-10 w-10 items-center justify-center rounded-full bg-dashboard-surface">
									<Clock className="h-5 w-5 text-dashboard-accent-dark" />
								</div>
								<div>
									<p className="font-display text-lg font-medium text-dashboard-text">
										{alreadyRequestedProject.status === "proposal_submitted"
											? t("designerDetail.proposalReceived")
											: t("designerDetail.requestSent")}
									</p>
									<p className="font-body text-sm text-dashboard-muted mt-0.5">
										{alreadyRequestedProject.status === "proposal_submitted"
											? t("designerDetail.proposalSubmittedForProject", {
													designerName: designer.name,
													projectTitle: alreadyRequestedProject.project.title,
												})
											: t("designerDetail.waitingOnProposalForProject", {
													projectTitle: alreadyRequestedProject.project.title,
												})}
									</p>
								</div>
							</div>
							<Button
								variant="primary"
								onClick={() =>
									navigate(`/projects/${alreadyRequestedProject.project.id}`)
								}
								className="shrink-0 h-10 rounded-xl px-5 font-mono text-[10px] font-bold uppercase tracking-wider text-dashboard-on-fill bg-dashboard-accent hover:bg-dashboard-accent-hover transition-all shadow-[var(--dashboard-shadow-1)] border-none"
							>
								{t("designerDetail.viewProject")}
							</Button>
						</div>
					</div>
				)}
			</div>

			<ImageLightbox
				images={portfolioImages.map((src, index) => ({
					src,
					alt: t("designerDetail.portfolioPhotoAlt", {
						name: designer.name,
						index: index + 1,
					}),
				}))}
				openIndex={lightboxIndex}
				onOpenIndexChange={setLightboxIndex}
			/>

			{/* Send Request Dialog Overlay */}
			{showRequestDialog && (
				<SendRequestDialog
					designer={designer}
					onClose={() => setShowRequestDialog(false)}
					onSuccess={handleRequestSuccess}
				/>
			)}
		</div>
	);
}
